import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MessageCircle, History, Send, Moon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { openWhatsApp } from "../../lib/whatsapp";
import { inputCls, PageHeader, StatusBadge } from "./shared";

const empty = { name: "", phone: "", email: "", notes: "", birthday: "", anniversary: "", gender: "", address: "", tag: "New" };

const TAG_STYLES = {
  VIP: "bg-[#C5A059]/20 text-[#8a6d33]",
  Regular: "bg-[#0A3F2F]/10 text-[#0A3F2F]",
  New: "bg-gray-100 text-gray-500",
};

const waLink = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return `https://wa.me/${digits.length === 10 ? "91" + digits : digits}`;
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState(null);
  const [mode, setMode] = useState("all");
  const [winback, setWinback] = useState(null);
  const [winDays, setWinDays] = useState(30);

  const load = useCallback(() => api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (mode !== "winback") return;
    api.get("/winback", { params: { days: winDays } }).then(({ data }) => setWinback(data)).catch(() => {});
  }, [mode, winDays]);

  const autoWinback = async (r) => {
    try {
      await api.post("/celebrations/send", { customer_id: r.customer_id, occasion: "winback" });
      toast.success(`Win-back message sent to ${r.name} via WhatsApp API`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const showHistory = async (c) => {
    try {
      const { data } = await api.get(`/customers/${c.id}/history`);
      setHistory(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, phone: c.phone, email: c.email || "", notes: c.notes || "",
      birthday: c.birthday || "", anniversary: c.anniversary || "", gender: c.gender || "",
      address: c.address || "", tag: c.tag || "New",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone required"); return; }
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success(editing ? "Customer updated" : "Customer added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/customers/${id}`); toast.success("Customer deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const filtered = customers.filter((c) => (c.name + c.phone).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="customers-page">
      <PageHeader title="Customers" subtitle="Your client relationship manager."
        action={
          <button onClick={openNew} data-testid="add-customer-btn"
            className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
            <Plus className="w-4 h-4" /> Add Customer
          </button>
        }
      />
      <div className="flex gap-2 flex-wrap items-center">
        <button onClick={() => setMode("all")} data-testid="customers-mode-all"
          className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors ${mode === "all" ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"}`}>
          All Clients
        </button>
        <button onClick={() => setMode("winback")} data-testid="customers-mode-winback"
          className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full transition-colors ${mode === "winback" ? "bg-[#0A3F2F] text-white" : "bg-white border border-[#E5E7E5] text-[#5C6A63] hover:border-[#0A3F2F]"}`}>
          <Moon className="w-3.5 h-3.5" /> Win-back needed{winback && mode === "winback" ? ` (${winback.rows.length})` : ""}
        </button>
        {mode === "winback" && (
          <select data-testid="winback-days-select" className="border border-[#E5E7E5] rounded-full text-xs font-semibold px-3 py-2 bg-white text-[#5C6A63] focus:outline-none"
            value={winDays} onChange={(e) => setWinDays(Number(e.target.value))}>
            {[30, 45, 60, 90].map((d) => <option key={d} value={d}>{`away ${d}+ days`}</option>)}
          </select>
        )}
      </div>
      {mode === "winback" && (
        <div className="space-y-3" data-testid="winback-section">
          <p className="text-sm text-[#5C6A63]">Regular clients (2+ visits) who haven't been back in {winDays}+ days — highest spenders first. Edit the win-back offer message in Settings → Greetings.</p>
          <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
                  <th className="px-4 py-3 font-semibold">Client</th>
                  <th className="px-4 py-3 font-semibold">Visits</th>
                  <th className="px-4 py-3 font-semibold">Last Visit</th>
                  <th className="px-4 py-3 font-semibold">Away For</th>
                  <th className="px-4 py-3 font-semibold">Total Spent</th>
                  <th className="px-4 py-3 font-semibold">Win Back</th>
                </tr>
              </thead>
              <tbody data-testid="winback-table-body">
                {(!winback || winback.rows.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-[#5C6A63]" data-testid="no-winback">🎉 No sleeping clients — everyone has visited within {winDays} days.</td></tr>
                )}
                {winback?.rows.map((r) => (
                  <tr key={r.customer_id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`winback-row-${r.customer_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-[#1C1C1C]">{r.name} <span className={`ml-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${TAG_STYLES[r.tag] || TAG_STYLES.New}`}>{r.tag}</span></p>
                      <p className="text-xs text-[#5C6A63]">{r.phone}</p>
                    </td>
                    <td className="px-4 py-3">{r.visits}</td>
                    <td className="px-4 py-3">{r.last_visit}</td>
                    <td className="px-4 py-3"><span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-600" data-testid={`winback-days-${r.customer_id}`}>{r.days_since} days</span></td>
                    <td className="px-4 py-3 font-semibold text-[#0A3F2F]">₹{r.total_spent.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openWhatsApp(r.phone, winback.message.replace("{name}", r.name))} data-testid={`winback-whatsapp-${r.customer_id}`}
                          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-600/40 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors">
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </button>
                        {winback.wa_configured && (
                          <button onClick={() => autoWinback(r)} data-testid={`winback-autosend-${r.customer_id}`}
                            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#0A3F2F] text-white hover:bg-[#0d5540] transition-colors">
                            <Send className="w-3.5 h-3.5" /> Auto-send
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {mode === "all" && (<>
      <input data-testid="customer-search-input" className={`${inputCls} max-w-sm`} placeholder="Find customer by name or mobile number…" value={search} onChange={(e) => setSearch(e.target.value)} />
      <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
              <th className="px-4 py-3 font-semibold">Name</th>
              <th className="px-4 py-3 font-semibold">Mobile</th>
              <th className="px-4 py-3 font-semibold">Tag</th>
              <th className="px-4 py-3 font-semibold">Visits</th>
              <th className="px-4 py-3 font-semibold">Total Spent</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="customers-table-body">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#5C6A63]" data-testid="no-customers">No customers found.</td></tr>
            )}
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#1C1C1C]">{c.name}</p>
                  <p className="text-xs text-[#5C6A63]">{c.email || ""}</p>
                </td>
                <td className="px-4 py-3">{c.phone}</td>
                <td className="px-4 py-3">
                  <span data-testid={`customer-tag-${c.id}`} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${TAG_STYLES[c.tag] || TAG_STYLES.New}`}>
                    {c.tag || "New"}
                  </span>
                </td>
                <td className="px-4 py-3">{c.total_visits}</td>
                <td className="px-4 py-3 font-semibold text-[#0A3F2F]">₹{(c.total_spent || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-3 items-center">
                    <button onClick={() => showHistory(c)} data-testid={`history-customer-${c.id}`} title="Visit history"
                      className="text-gray-400 hover:text-[#0A3F2F] transition-colors"><History className="w-4 h-4" /></button>
                    <a href={waLink(c.phone)} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-customer-${c.id}`} title="Message on WhatsApp"
                      className="text-gray-400 hover:text-emerald-600 transition-colors"><MessageCircle className="w-4 h-4" /></a>
                    <button onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`} title="Edit"
                      className="text-gray-400 hover:text-[#0A3F2F] transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(c.id)} data-testid={`delete-customer-${c.id}`} title="Delete"
                      className="text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="customer-form">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="customer-name-input" className={inputCls} placeholder="Name *" value={form.name} onChange={set("name")} />
              <input data-testid="customer-phone-input" className={inputCls} placeholder="Mobile number *" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="customer-email-input" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} />
              <select data-testid="customer-gender-select" className={inputCls} value={form.gender} onChange={set("gender")}>
                <option value="">Gender</option>
                <option>Female</option><option>Male</option><option>Other</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Birthday</label>
                <input data-testid="customer-birthday-input" type="date" className={inputCls} value={form.birthday} onChange={set("birthday")} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Anniversary</label>
                <input data-testid="customer-anniversary-input" type="date" className={inputCls} value={form.anniversary} onChange={set("anniversary")} />
              </div>
            </div>
            <input data-testid="customer-address-input" className={inputCls} placeholder="Address" value={form.address} onChange={set("address")} />
            <select data-testid="customer-tag-select" className={inputCls} value={form.tag} onChange={set("tag")}>
              <option value="New">Tag: New</option>
              <option value="Regular">Tag: Regular</option>
              <option value="VIP">Tag: VIP</option>
            </select>
            <textarea data-testid="customer-notes-input" className={inputCls} rows={2} placeholder="Notes" value={form.notes} onChange={set("notes")} />
            <button type="submit" data-testid="customer-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {editing ? "Save Changes" : "Add Customer"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {history?.customer?.name} — Visit History
              <span className="block text-sm font-normal text-[#5C6A63] mt-1">
                {history?.customer?.phone}{history?.customer?.tag ? ` · ${history.customer.tag}` : ""}
                {history?.customer?.birthday ? ` · Birthday: ${history.customer.birthday}` : ""}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6" data-testid="customer-history">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C6A63] mb-3">Appointments ({history?.appointments?.length || 0})</p>
              {history?.appointments?.length === 0 && <p className="text-sm text-[#5C6A63]">No appointments yet.</p>}
              <div className="space-y-2">
                {history?.appointments?.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-3 bg-[#FAFAF8] rounded-md px-4 py-2.5 text-sm">
                    <div>
                      <p className="font-semibold text-[#1C1C1C]">{a.service_name}</p>
                      <p className="text-xs text-[#5C6A63]">{a.date} · {a.time} · {a.staff_name}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-[#0A3F2F]">₹{a.price.toLocaleString("en-IN")}</span>
                      <StatusBadge status={a.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C6A63] mb-3">Bills ({history?.bills?.length || 0})</p>
              {history?.bills?.length === 0 && <p className="text-sm text-[#5C6A63]">No bills yet.</p>}
              <div className="space-y-2">
                {history?.bills?.map((b) => (
                  <div key={b.id} className={`flex items-center justify-between gap-3 bg-[#FAFAF8] rounded-md px-4 py-2.5 text-sm ${b.voided ? "opacity-50" : ""}`}>
                    <div>
                      <p className="font-semibold text-[#1C1C1C]">{b.invoice_no} {b.voided && <span className="text-[10px] font-bold text-red-600">VOID</span>}</p>
                      <p className="text-xs text-[#5C6A63]">{b.date} · {b.items.length} item{b.items.length > 1 ? "s" : ""} · {b.payment_method}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-[#0A3F2F]">₹{b.total.toLocaleString("en-IN")}</span>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${b.paid ? "bg-emerald-600/10 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                        {b.paid ? "Paid" : "Unpaid"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
